import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  collection, query, where, onSnapshot, addDoc, orderBy,
  doc, updateDoc, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { AGENCIAS } from "../services/parseDescripcionPedido";
import { generarEImprimirEtiquetas } from "../services/etiquetasPDF";
import { unidadesDeItem } from "../services/etiquetasFS";
import QrScanner from "../../../components/QrScanner";
import EscaneoContinuoModal from "../components/EscaneoContinuoModal";

// ── Helpers ───────────────────────────────────────────────
function startOfDay(d = new Date()) {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(s);
}

function startOfWeek() {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
}

function fmtHora(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
}

function estadoLabel(estado) {
  const map = {
    PENDIENTE_ASIGNAR: "Pendiente asignar",
    ASIGNADO: "Asignado",
    EN_PREPARACION: "En preparación",
    CON_ERROR: "Con error",
    PREPARADO: "Preparado",
    CONTROLADO: "Controlado",
  };
  return map[estado] || estado?.replace(/_/g, " ") || "—";
}

// ── Modal de escaneo de bultos + paquetes ─────────────────
function ScanModal({ pedido, onClose, onCargado, collection: col = "pedidos_despachados" }) {
  // Unidades reales del pedido: bultos (accesorios) + paquetes (perfiles).
  const unidades = useMemo(() => unidadesDeItem({ pedidoId: pedido.id, bultos: pedido.bultos, paquetes: pedido.paquetes }), [pedido.id, pedido.bultos, pedido.paquetes]);
  const totalUnidades = unidades.length || 1;
  const [escaneados, setEscaneados] = useState(new Set()); // set de KEYS escaneadas
  const [error, setError]           = useState("");
  const [saving, setSaving]         = useState(false);

  const handleScan = useCallback((raw) => {
    setError("");
    const texto = (typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw)).trim();
    const u = unidades.find(x => x.key === texto);
    if (!u) {
      if (texto.startsWith(pedido.id)) { setError(`Etiqueta que no corresponde a esta carga`); }
      else { setError(`Etiqueta de otro pedido`); }
      return;
    }
    setEscaneados(prev => {
      if (prev.has(u.key)) { setError(`${u.label} ya escaneado`); return prev; }
      return new Set([...prev, u.key]);
    });
  }, [pedido.id, unidades]);

  const todosEscaneados = escaneados.size === totalUnidades;

  async function confirmarCarga() {
    setSaving(true);
    try {
      await updateDoc(doc(db, col, pedido.id), {
        cargaEstado: "CARGADO",
        cargadoAt: serverTimestamp(),
        bultosEscaneados: [...escaneados],
      });
      onCargado();
    } catch (e) {
      setError("Error al guardar: " + (e?.message || e));
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", width: "100%",
          borderRadius: "16px 16px 0 0", padding: "20px 20px 32px",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
              📷 Cargar — #{pedido.numero}
            </h3>
            <p style={{ margin: "3px 0 0", fontSize: "13px", color: "#64748b" }}>{pedido.cliente}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
        </div>

        {/* Las etiquetas se imprimen en el control (encargado). Acá solo se escanean
            para verificar la carga: bultos (accesorios) + paquetes (perfiles). */}
        <>
            {/* Progreso de unidades (bultos + paquetes) */}
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
              {unidades.map(u => {
                const ok = escaneados.has(u.key);
                const esPaq = u.tipo === "paquete";
                return (
                  <div key={u.key} title={u.label} style={{
                    minWidth: "36px", height: "36px", padding: "0 6px", borderRadius: "8px",
                    background: ok ? "#16a34a" : esPaq ? "#eff6ff" : "#f1f5f9",
                    color: ok ? "#fff" : esPaq ? "#1d4ed8" : "#94a3b8",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: "13px",
                    border: `1.5px solid ${ok ? "#16a34a" : esPaq ? "#bfdbfe" : "#e2e8f0"}`,
                  }}>
                    {ok ? "✓" : (esPaq ? "📏" : "📦") + u.indice}
                  </div>
                );
              })}
            </div>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#64748b" }}>
              {escaneados.size}/{totalUnidades} unidades escaneadas
              {" "}({Number(pedido.bultos) || 0} bultos · {Number(pedido.paquetes) || 0} paquetes)
            </p>

            {error && (
              <div style={{
                background: "#fee2e2", border: "1px solid #fca5a5",
                borderRadius: "8px", padding: "10px 14px",
                color: "#dc2626", fontSize: "13px", fontWeight: 600, marginBottom: "12px",
              }}>⚠️ {error}</div>
            )}

            {!todosEscaneados && (
              <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />
            )}

            {todosEscaneados && (
              <div style={{
                background: "#f0fdf4", border: "1.5px solid #86efac",
                borderRadius: "12px", padding: "14px 16px", marginBottom: "16px", textAlign: "center",
              }}>
                <div style={{ fontSize: "28px", marginBottom: "6px" }}>✅</div>
                <p style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: "15px" }}>
                  Todas las unidades escaneadas
                </p>
              </div>
            )}

            <button
              onClick={confirmarCarga}
              disabled={!todosEscaneados || saving}
              style={{
                width: "100%", padding: "15px", borderRadius: "12px", border: "none",
                fontWeight: 700, fontSize: "15px", marginTop: "8px",
                background: todosEscaneados && !saving ? "#0f172a" : "#e2e8f0",
                color: todosEscaneados && !saving ? "#fff" : "#94a3b8",
                cursor: todosEscaneados && !saving ? "pointer" : "not-allowed",
              }}
            >
              {saving ? "Confirmando…" : "✓ Confirmar carga"}
            </button>
        </>
      </div>
    </div>
  );
}

// ── Tarjeta de pedido despachado ──────────────────────────
function PedidoCard({ p, onScan }) {
  const esCargado     = p.cargaEstado === "CARGADO";
  const esDiscrepancia = p.cargaEstado === "DISCREPANCIA";

  return (
    <div style={{
      background: esCargado ? "#f0fdf4" : esDiscrepancia ? "#fff7ed" : "#fff",
      border: "1.5px solid",
      borderColor: esCargado ? "#86efac" : esDiscrepancia ? "#fed7aa" : "#e2e8f0",
      borderRadius: "12px", padding: "14px 16px",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
              {p.numero || p.id}
            </span>
            {p.despachadoAt && (
              <span style={{ fontSize: "11px", color: "#94a3b8" }}>{fmtHora(p.despachadoAt)}</span>
            )}
          </div>
          <div style={{ fontSize: "13px", color: "#334155", marginTop: "2px", fontWeight: 500 }}>{p.cliente}</div>
          <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#0f172a" }}>
              📫 {p.bultos ?? "—"} bulto{Number(p.bultos) !== 1 ? "s" : ""}
            </span>
          </div>
          {p.bultosEscaneados?.length > 0 && (
            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "3px" }}>
              Escaneados: {p.bultosEscaneados.join(", ")}
            </div>
          )}
        </div>

        <div style={{ flexShrink: 0 }}>
          {esCargado ? (
            <span style={{
              fontSize: "13px", fontWeight: 700, color: "#166534",
              background: "#dcfce7", border: "1px solid #86efac",
              borderRadius: "8px", padding: "6px 12px",
            }}>✓ Cargado</span>
          ) : esDiscrepancia ? (
            <span style={{
              fontSize: "13px", fontWeight: 700, color: "#92400e",
              background: "#fef3c7", border: "1px solid #fde68a",
              borderRadius: "8px", padding: "6px 10px",
            }}>⚠️ Diferencia</span>
          ) : (
            <button
              onClick={() => onScan(p)}
              style={{
                padding: "9px 16px", fontSize: "13px", fontWeight: 700,
                background: "#0f172a", color: "#fff",
                border: "none", borderRadius: "9px", cursor: "pointer",
              }}
            >
              📷 Escanear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────
export default function ControlCarga() {
  const { profile } = useAuth();
  const { confirmarAvance } = useApp();
  const navigate    = useNavigate();

  const [despachados,  setDespachados]  = useState([]);
  const [pendientes,   setPendientes]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toastMsg,     setToastMsg]     = useState(null);
  const [scanPedido,   setScanPedido]   = useState(null);
  const [imprimiendo,  setImprimiendo]  = useState({}); // { [agencia]: bool }
  const [collapsed,    setCollapsed]    = useState(new Set()); // agencias colapsadas
  const [showFinalizar, setShowFinalizar] = useState(false);
  const [showEscaneo,  setShowEscaneo]  = useState(false);
  const [cargasGuardadas, setCargasGuardadas] = useState([]); // cargas de agencia finalizadas (historial)
  const [verHistorial, setVerHistorial] = useState(false);
  const [histAbierta, setHistAbierta]   = useState(null); // id de carga expandida

  const showToast = (msg, tipo = "ok") => {
    setToastMsg({ msg, tipo });
    setTimeout(() => setToastMsg(null), 3000);
  };

  const depositoUsuario = profile?.deposito || null;

  // Listener 1: pedidos_despachados AGENCIA (esta semana)
  useEffect(() => {
    const desde = startOfWeek();
    const q = query(
      collection(db, "pedidos_despachados"),
      where("metodoEntrega", "==", "AGENCIA"),
    );
    const unsub = onSnapshot(q, snap => {
      const desdeMs = desde.toMillis();
      const todos = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p => !(depositoUsuario && p.deposito && p.deposito !== depositoUsuario));
      const docs = todos.filter(p => {
        const at = p.despachadoAt;
        if (!at) return true;
        const ms = at?.seconds ? at.seconds * 1000 : new Date(at).getTime();
        return ms >= desdeMs;
      });
      setDespachados(docs);
      setLoading(false);
    });
    return () => unsub();
  }, [depositoUsuario]);

  // Listener: cargas de agencia finalizadas (historial guardado)
  useEffect(() => {
    const qy = query(collection(db, "cargas_agencia"), orderBy("finalizadoAt", "desc"));
    const unsub = onSnapshot(qy, snap => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => !depositoUsuario || c.deposito === depositoUsuario);
      setCargasGuardadas(list);
    }, err => console.error("[cargas_agencia]", err));
    return () => unsub();
  }, [depositoUsuario]);

  // Listener 2: pedidos activos AGENCIA
  useEffect(() => {
    if (!depositoUsuario) return;
    const q = query(
      collection(db, "pedidos"),
      where("metodoEntrega", "==", "AGENCIA"),
      where("deposito",      "==", depositoUsuario),
    );
    const unsub = onSnapshot(q, snap => {
      setPendientes(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.estado !== "ANULADO"),
      );
    });
    return () => unsub();
  }, [depositoUsuario]);

  // Imprimir etiquetas de los pedidos DESPACHADOS de una agencia (post-despacho,
  // ya son definitivos). Marca etiquetasImpresas en pedidos_despachados.
  async function imprimirEtiquetasAgencia(agNombre, pedidosDespachados) {
    setImprimiendo(prev => ({ ...prev, [agNombre]: true }));
    try {
      const items = pedidosDespachados.map(p => ({
        pedidoId: p.id,
        numero: p.numero || p.id,
        cliente: p.cliente || "",
        direccion: p.agencia || p.metodoEntrega || "AGENCIA",
        localidad: p.deposito || "",
        bultos: Number(p.bultos) || 1,
      }));
      await generarEImprimirEtiquetas(items);
      await Promise.all(pedidosDespachados.map(p =>
        updateDoc(doc(db, "pedidos_despachados", p.id), { etiquetasImpresas: true })
      ));
      showToast(`Etiquetas de ${agNombre} enviadas a imprimir`);
    } catch (e) {
      showToast("Error al imprimir: " + (e?.message || e), "error");
    } finally {
      setImprimiendo(prev => ({ ...prev, [agNombre]: false }));
    }
  }

  // Agrupar por agencia
  const agenciaOrder = Object.fromEntries(AGENCIAS.map(a => [a.nombre, a.orden]));
  const hoy = startOfDay();
  const despHoy        = despachados.filter(p => p.despachadoAt?.seconds >= hoy.seconds);
  const despAnteriores = despachados.filter(p =>
    p.despachadoAt?.seconds < hoy.seconds && p.cargaEstado !== "CARGADO"
  );
  const despMostrar = [...despHoy, ...despAnteriores];

  const porAgencia = {};
  for (const p of despMostrar) {
    const ag = p.agencia || "SIN ASIGNAR";
    if (!porAgencia[ag]) porAgencia[ag] = { despachados: [], pendientes: [] };
    porAgencia[ag].despachados.push(p);
  }
  for (const p of pendientes) {
    const ag = p.agencia || "SIN ASIGNAR";
    if (!porAgencia[ag]) porAgencia[ag] = { despachados: [], pendientes: [] };
    const yaDesp = despMostrar.some(d => d.id === p.id);
    if (!yaDesp) porAgencia[ag].pendientes.push(p);
  }

  const agenciasOrdenadas = Object.keys(porAgencia).sort((a, b) => {
    const oa = agenciaOrder[a] ?? 99;
    const ob = agenciaOrder[b] ?? 99;
    return oa - ob;
  });

  const totalDespachados   = despMostrar.length;
  const totalCargados      = despMostrar.filter(p => p.cargaEstado === "CARGADO").length;
  const totalDiscrepancias = despMostrar.filter(p => p.cargaEstado === "DISCREPANCIA").length;
  const todoCargado = totalDespachados > 0 && (totalCargados + totalDiscrepancias) === totalDespachados;
  const totalPendientes = pendientes.length;

  // Para "Finalizar carga del día"
  const sinCargar = despMostrar.filter(p => p.cargaEstado !== "CARGADO" && p.cargaEstado !== "DISCREPANCIA");
  const quedanAfuera = sinCargar.length + totalPendientes;

  // Cierre del día: guarda en el historial (cargas_agencia) los pedidos cargados
  // hoy, agrupados por agencia, con fecha/hora y quién finalizó. No imprime.
  async function finalizarCargaDelDia() {
    const cargadosHoy = despMostrar.filter(p => p.cargaEstado === "CARGADO");
    if (cargadosHoy.length === 0) {
      showToast("No hay pedidos cargados para finalizar", "warn");
      setShowFinalizar(false);
      return;
    }
    // Agrupar por agencia
    const porAg = {};
    for (const p of cargadosHoy) {
      const ag = p.agencia || "SIN ASIGNAR";
      (porAg[ag] ||= []).push({
        id: p.id,
        numero: p.numero || p.id,
        cliente: p.cliente || "",
        bultos: Number(p.bultos) || 1,
      });
    }
    const agencias = Object.entries(porAg)
      .map(([agencia, pedidos]) => ({
        agencia,
        pedidos,
        totalBultos: pedidos.reduce((s, x) => s + x.bultos, 0),
      }))
      .sort((a, b) => (agenciaOrder[a.agencia] ?? 99) - (agenciaOrder[b.agencia] ?? 99));

    try {
      await addDoc(collection(db, "cargas_agencia"), {
        deposito: depositoUsuario || null,
        finalizadoAt: serverTimestamp(),
        finalizadoPorUid: profile?.uid || null,
        finalizadoPorNombre: profile?.nombre || profile?.email || null,
        totalPedidos: cargadosHoy.length,
        totalBultos: cargadosHoy.reduce((s, p) => s + (Number(p.bultos) || 1), 0),
        agencias,
      });
      setShowFinalizar(false);
      confirmarAvance({
        titulo: "Carga finalizada",
        mensaje: `${cargadosHoy.length} pedido${cargadosHoy.length !== 1 ? "s" : ""} guardado${cargadosHoy.length !== 1 ? "s" : ""} en el historial`,
        onOk: () => {},
      });
    } catch (e) {
      showToast("Error al guardar: " + (e?.message || e), "error");
    }
  }

  if (loading) return (
    <div className="container screen-center">⏳ Cargando pedidos de agencia…</div>
  );

  return (
    <div className="container" style={{ paddingBottom: "80px" }}>

      {toastMsg && (
        <div className="toast" style={{
          background: toastMsg.tipo === "ok" ? "var(--ok)" : toastMsg.tipo === "warn" ? "#f59e0b" : "var(--error)",
        }}>
          {toastMsg.msg}
        </div>
      )}

      {/* Header */}
      <header className="topbar card" style={{ marginBottom: "16px" }} id="control-header">
        <button onClick={() => navigate("/encargado/cola-despacho")} className="btn btn--ghost btn-sm">
          ⬅ Volver
        </button>
        <div>
          <h1 className="h1" style={{ margin: 0, fontSize: "17px" }}>🚌 Agencias</h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            {new Date().toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {totalDespachados > 0 && (
            <span className={`pill ${todoCargado ? "pill--ok" : "pill--warn"}`}>
              {totalCargados}/{totalDespachados} cargados
            </span>
          )}
          {totalPendientes > 0 && (
            <span className="pill" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
              {totalPendientes} por despachar
            </span>
          )}
          <button onClick={() => window.print()} className="btn btn--ghost btn-sm" title="Imprimir manifiesto">🖨️</button>
        </div>
      </header>

      {/* Escaneo continuo: escanear todos los bultos seguidos sin entrar a cada pedido */}
      {sinCargar.length > 0 && (
        <button
          onClick={() => setShowEscaneo(true)}
          style={{
            width: "100%", padding: "14px", marginBottom: "16px",
            borderRadius: "12px", border: "none", background: "#0f172a", color: "#fff",
            fontWeight: 800, fontSize: "15px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          }}
        >
          📷 Escanear todo ({sinCargar.length} pedido{sinCargar.length !== 1 ? "s" : ""} para cargar)
        </button>
      )}

      {totalDespachados === 0 && totalPendientes === 0 && (
        <div style={{ textAlign: "center", padding: "64px 20px", color: "#94a3b8" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
          <strong style={{ color: "#334155", display: "block", fontSize: "16px", marginBottom: "6px" }}>
            No hay pedidos de agencia
          </strong>
        </div>
      )}

      {despAnteriores.length > 0 && (
        <div style={{
          background: "#fff7ed", border: "1.5px solid #fed7aa",
          borderRadius: "10px", padding: "10px 14px", marginBottom: "16px",
          fontSize: "13px", color: "#92400e",
        }}>
          ⚠️ Hay <strong>{despAnteriores.length} pedido{despAnteriores.length > 1 ? "s" : ""}</strong> de días anteriores sin cargar.
        </div>
      )}

      {/* Secciones por agencia */}
      <div id="manifiesto-contenido" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {agenciasOrdenadas.map(agNombre => {
          const { despachados: desps, pendientes: pends } = porAgencia[agNombre];

          // DESPACHADOS sin etiquetas impresas → se pueden imprimir en lote
          // (post-despacho: ya son definitivos).
          const paraimprimir = desps.filter(p => Number(p.bultos) > 0 && !p.etiquetasImpresas);
          // Pre-despacho: informativo, no se imprime hasta despachar.
          const enPrep = pends;

          const cargados      = desps.filter(p => p.cargaEstado === "CARGADO").length;
          const discrepancias = desps.filter(p => p.cargaEstado === "DISCREPANCIA").length;
          const listos        = cargados + discrepancias;
          const todoListo     = desps.length > 0 && listos === desps.length;
          const ag = AGENCIAS.find(a => a.nombre === agNombre);

          const totalBto = desps.reduce((s, p) => s + (Number(p.bultos) || 0), 0);
          const printing = imprimiendo[agNombre];

          const isCollapsed = collapsed.has(agNombre);
          const toggleCollapse = () => setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(agNombre)) next.delete(agNombre);
            else next.add(agNombre);
            return next;
          });

          return (
            <div key={agNombre} className="card" style={{ padding: 0, overflow: "hidden" }}>
              {/* Header de agencia — siempre visible, clickeable para colapsar */}
              <button
                onClick={toggleCollapse}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "12px 16px", border: "none", cursor: "pointer",
                  background: todoListo && desps.length > 0 ? "#f0fdf4" : "#f8fafc",
                  borderBottom: isCollapsed ? "none" : "1px solid #e2e8f0",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "14px", color: "#94a3b8" }}>{isCollapsed ? "▸" : "▾"}</span>
                    <span style={{ fontWeight: 800, fontSize: "15px", color: "#0f172a" }}>
                      {ag ? `${ag.orden}. ${agNombre}` : agNombre}
                    </span>
                    {desps.length > 0 && (
                      <span style={{ fontSize: "12px", color: "#64748b" }}>
                        📫 {totalBto} bulto{totalBto !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
                    {desps.length > 0 && (
                      <span style={{
                        fontSize: "12px", fontWeight: 600,
                        color: todoListo ? "#166534" : "#64748b",
                        background: todoListo ? "#dcfce7" : "#f1f5f9",
                        padding: "3px 10px", borderRadius: "20px",
                      }}>
                        {todoListo ? "✓ Completo" : `${listos}/${desps.length} cargados`}
                      </span>
                    )}
                    {pends.length > 0 && (
                      <span style={{
                        fontSize: "12px", fontWeight: 600,
                        background: "#eff6ff", color: "#1d4ed8",
                        padding: "3px 10px", borderRadius: "20px",
                      }}>
                        {pends.length} por despachar
                      </span>
                    )}
                  </div>
                </div>
              </button>

              {/* Contenido colapsable */}
              {!isCollapsed && (
                <>
                  {/* Botón imprimir todas las etiquetas de los despachados de esta agencia */}
                  {paraimprimir.length > 0 && (
                    <div style={{ padding: "0 12px 12px", background: todoListo && desps.length > 0 ? "#f0fdf4" : "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                      <button
                        onClick={e => { e.stopPropagation(); imprimirEtiquetasAgencia(agNombre, paraimprimir); }}
                        disabled={printing}
                        style={{
                          width: "100%", padding: "10px 16px", borderRadius: "8px", border: "none",
                          background: printing ? "#e2e8f0" : "#1d4ed8",
                          color: printing ? "#94a3b8" : "#fff",
                          fontWeight: 700, fontSize: "13px",
                          cursor: printing ? "not-allowed" : "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                        }}
                      >
                        {printing ? "Imprimiendo…" : `🖨️ Imprimir todas las etiquetas (${paraimprimir.length})`}
                      </button>
                    </div>
                  )}

                  {/* Pedidos DESPACHADOS → escanear */}
                  {desps.length > 0 && (
                    <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      {desps.length > 0 && (
                        <p style={{ margin: "0 0 4px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          📦 Para cargar ({desps.length})
                        </p>
                      )}
                      {desps.map(p => (
                        <PedidoCard key={p.id} p={p} onScan={setScanPedido} />
                      ))}
                    </div>
                  )}

                  {/* Pedidos en preparación (no CONTROLADO) */}
                  {enPrep.length > 0 && (
                    <>
                      {(desps.length > 0 || paraimprimir.length > 0) && (
                        <div style={{ borderTop: "1px dashed #cbd5e1", margin: "0 12px" }} />
                      )}
                      <div style={{ padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                        <p style={{ margin: "0 0 6px", fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          🕐 En preparación ({enPrep.length})
                        </p>
                        {enPrep.map(p => (
                          <div key={p.id} style={{
                            background: "#f8fafc", border: "1.5px solid #e2e8f0",
                            borderRadius: "10px", padding: "11px 14px",
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            opacity: 0.85,
                          }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: "13px", color: "#334155" }}>
                                {p.numero || p.id}
                              </div>
                              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>{p.cliente}</div>
                            </div>
                            <span style={{
                              fontSize: "11px", fontWeight: 600,
                              background: "#f1f5f9", color: "#64748b",
                              padding: "4px 10px", borderRadius: "20px",
                            }}>
                              {estadoLabel(p.estado)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Historial de cargas finalizadas */}
      {cargasGuardadas.length > 0 && (
        <div style={{ marginTop: "24px" }}>
          <button
            onClick={() => setVerHistorial(v => !v)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: "13px", color: "#64748b", padding: "4px 0",
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            <span>{verHistorial ? "▾" : "▸"}</span>
            📋 Historial de cargas finalizadas ({cargasGuardadas.length})
          </button>

          {verHistorial && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "12px" }}>
              {cargasGuardadas.map(c => {
                const fecha = c.finalizadoAt?.toDate ? c.finalizadoAt.toDate() : null;
                const abierta = histAbierta === c.id;
                return (
                  <div key={c.id} className="card" style={{ padding: "12px 14px" }}>
                    <button
                      onClick={() => setHistAbierta(abierta ? null : c.id)}
                      style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: 0, textAlign: "left" }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: "14px", color: "#0f172a", textTransform: "capitalize" }}>
                          {fecha ? fecha.toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" }) : "Sin fecha"}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                          {fecha ? `🕐 ${fecha.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" })}` : ""}
                          {c.finalizadoPorNombre ? ` · 👤 ${c.finalizadoPorNombre}` : ""}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "12px", fontWeight: 700, color: "#166534", background: "#dcfce7", padding: "3px 10px", borderRadius: "20px" }}>
                          {c.totalPedidos} pedido{c.totalPedidos !== 1 ? "s" : ""}
                        </span>
                        <span style={{ color: "#94a3b8", fontSize: "14px" }}>{abierta ? "▲" : "▼"}</span>
                      </div>
                    </button>

                    {abierta && (
                      <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                        {(c.agencias || []).map((ag, i) => (
                          <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "9px 11px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                              <span style={{ fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>🚌 {ag.agencia}</span>
                              <span style={{ fontSize: "11px", color: "#64748b" }}>{ag.pedidos?.length || 0} ped. · {ag.totalBultos} bultos</span>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                              {(ag.pedidos || []).map(p => (
                                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "12.5px" }}>
                                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    <strong style={{ color: "#0f172a" }}>{p.numero}</strong>
                                    <span style={{ color: "#64748b", marginLeft: "6px" }}>{p.cliente}</span>
                                  </span>
                                  <span style={{ flexShrink: 0, color: "#94a3b8" }}>📫 {p.bultos}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Botón finalizar carga del día */}
      {totalCargados > 0 && (
        <div className="cta-fija" style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "14px 20px", background: "rgba(255,255,255,0.95)",
          borderTop: "1px solid #e2e8f0", backdropFilter: "blur(8px)",
        }}>
          <button
            onClick={() => setShowFinalizar(true)}
            style={{
              width: "100%", padding: "15px", fontSize: "15px", fontWeight: 800,
              borderRadius: "12px", border: "none", background: "#16a34a", color: "#fff",
              cursor: "pointer",
            }}
          >
            🚚 Finalizar carga del día {quedanAfuera > 0 ? `(${quedanAfuera} sin cargar)` : ""}
          </button>
        </div>
      )}

      {/* Modal: finalizar carga del día */}
      {showFinalizar && (
        <div
          onClick={() => setShowFinalizar(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "#fff", width: "100%", borderRadius: "16px 16px 0 0", padding: "20px 20px 28px", maxHeight: "90vh", overflowY: "auto" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800 }}>🚚 Finalizar carga del día</h3>
              <button onClick={() => setShowFinalizar(false)} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
            </div>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#64748b" }}>
              Vas a cerrar la carga de agencia de hoy. Queda guardada en el historial con la fecha, hora y los pedidos por agencia.
            </p>

            <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "12px", padding: "12px 14px", marginBottom: "12px" }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: "14px", color: "#166534" }}>
                ✅ Cargados hoy: {totalCargados} pedido{totalCargados !== 1 ? "s" : ""}
              </p>
            </div>

            {quedanAfuera > 0 ? (
              <div style={{ background: "#fff7ed", border: "1.5px solid #fdba74", borderRadius: "12px", padding: "14px 16px", marginBottom: "16px" }}>
                <p style={{ margin: "0 0 8px", fontWeight: 800, fontSize: "14px", color: "#9a3412" }}>
                  ⚠️ Quedan {quedanAfuera} sin cargar
                </p>
                {sinCargar.length > 0 && (
                  <p style={{ margin: "0 0 4px", fontSize: "12px", color: "#7c2d12" }}>
                    📦 {sinCargar.length} despachado{sinCargar.length !== 1 ? "s" : ""} sin escanear
                  </p>
                )}
                {totalPendientes > 0 && (
                  <p style={{ margin: 0, fontSize: "12px", color: "#7c2d12" }}>
                    🕐 {totalPendientes} todavía sin despachar
                  </p>
                )}
                <p style={{ margin: "10px 0 0", fontSize: "12px", color: "#9a3412", fontStyle: "italic" }}>
                  Estos vuelven a aparecer mañana para la próxima carga.
                </p>
              </div>
            ) : (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "10px 14px", marginBottom: "16px", fontSize: "13px", color: "#166534" }}>
                No queda nada sin cargar. Todo lo de agencia de hoy salió.
              </div>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowFinalizar(false)}
                style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, fontSize: "14px", color: "#475569", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                onClick={finalizarCargaDelDia}
                style={{ flex: 2, padding: "13px", borderRadius: "12px", border: "none", background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: "14px", cursor: "pointer" }}
              >
                ✅ Finalizar y guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal scanner (un pedido) */}
      {scanPedido && (
        <ScanModal
          pedido={scanPedido}
          onClose={() => setScanPedido(null)}
          onCargado={() => {
            const num = scanPedido?.numero || scanPedido?.id;
            setScanPedido(null);
            confirmarAvance({
              titulo: "Pedido cargado",
              mensaje: `#${num} cargado · queda en el camión`,
              onOk: () => navigate("/pedidos"),
            });
          }}
        />
      )}

      {/* Escaneo continuo (todos los bultos seguidos) */}
      {showEscaneo && (
        <EscaneoContinuoModal
          pedidos={sinCargar}
          onClose={() => setShowEscaneo(false)}
        />
      )}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #manifiesto-contenido, #manifiesto-contenido * { visibility: visible; }
          #control-header { visibility: visible !important; }
          #manifiesto-contenido { position: absolute; left: 0; top: 60px; width: 100%; padding: 0 20px; }
          #control-header { position: absolute; top: 0; left: 0; right: 0; box-shadow: none !important; padding: 8px 20px; }
          .btn { display: none !important; }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
}
